import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Home, FileText, Scale, Settings, BarChart3, SearchCheck, FlaskConical } from 'lucide-react';
import { Toaster } from 'sonner';

import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../utils/rbac';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { useAccess, Capability } from '../../hooks/useAccess';
import { JunoProvider } from '../../contexts/JunoContext';
import { PageHeaderProvider } from '../../contexts/PageHeaderContext';
import { NavigationProvider } from '../../contexts/NavigationContext';
import UserProfile from '../v3/user/UserProfile';
import CrossOrgBar from './CrossOrgBar';
import { cn } from '../../lib/utils';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPaths?: string[];
  /** Legacy `role === 'admin'` gate (Evaluate — INTERNAL surface). */
  adminOnly?: boolean;
  /** GO Lab gate — legacy admins OR a GO Lab capability (PLATFORM_ADMIN or a
   *  LAB_* role). Gated on `golab.annotate`, which LAB roles hold but
   *  `platform.manage` would miss. */
  golab?: boolean;
  /** CLIENT firm-administration gate — legacy admins OR FIRM_ADMIN (firm.manage). */
  firmAdmin?: boolean;
  /** Internal platform surfaces (Sep-3): platform administrators only. */
  platform?: boolean;
  /** Monitoring surface — legacy admins OR holders of `monitoring.view`. Hides
   *  the Monitoring screens by default from users with no Monitoring cap (e.g.
   *  Lab-only or ombudsman-only roles, or orgs without the Monitoring feature). */
  monitoring?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Today', path: '/v4/today', icon: Home, matchPaths: ['/v4/today'], monitoring: true },
  { label: 'Conduct Records', path: '/v4/records', icon: FileText, matchPaths: ['/v4/records'], monitoring: true },
  {
    label: 'Case Guidance',
    path: '/v3/ombudsman',
    icon: Scale,
    matchPaths: ['/v3/ombudsman', '/v3/workspace/ombudsman'],
    // Sep-3: cases/FOS tooling is internal — platform administrators only.
    platform: true,
  },
  // Sep-10: Analyst Canvas (Reports) is an internal surface — platform admins
  // only for now (was ombudsman/fos_analyst/legacy-admin, which let org admins in).
  { label: 'Reports', path: '/v4/reports', icon: BarChart3, matchPaths: ['/v4/reports'], platform: true },
  { label: 'Evaluate', path: '/v4/evaluate', icon: SearchCheck, matchPaths: ['/v4/evaluate'], platform: true },
  {
    label: 'Labs',
    path: '/v4/go-lab',
    icon: FlaskConical,
    matchPaths: ['/v4/go-lab'],
    golab: true,
  },
  {
    label: 'Settings',
    path: '/v3/settings',
    icon: Settings,
    matchPaths: ['/v3/settings'],
    firmAdmin: true,
  },
];

const GoodOutcomesMark: React.FC = () => (
  <svg className="h-9 w-9" fill="none" viewBox="0 0 62 62" aria-hidden="true">
    <path
      d="M47.5807 0H14.4193C6.45575 0 0 6.45575 0 14.4193V47.5807C0 55.5443 6.45575 62 14.4193 62H47.5807C55.5443 62 62 55.5443 62 47.5807V14.4193C62 6.45575 55.5443 0 47.5807 0Z"
      fill="#4342F5"
    />
    <path
      d="M30.428 50.4676C25.7751 50.4676 21.199 48.7554 17.6748 45.5306C17.0529 44.9625 17.0068 43.9874 17.575 43.3654C18.1432 42.7435 19.1183 42.6975 19.7402 43.2656C23.9247 47.0893 29.8828 48.3792 35.2805 46.6363C36.0867 46.3752 36.9543 46.8206 37.2077 47.6267C37.4687 48.4329 37.0234 49.2929 36.2172 49.5539C34.3207 50.1682 32.3628 50.4676 30.4203 50.4676H30.428Z"
      fill="white"
    />
    <path
      d="M30.4894 41.1695C27.1264 41.1695 23.8249 39.9871 21.2067 37.7759C16.001 33.3533 14.5268 26.804 17.3523 20.6846C19.8477 15.2946 25.9747 10.4421 34.0367 11.7704C37.0004 12.2541 40.133 13.6515 42.8741 15.6939C43.519 16.1776 44.7475 17.3063 45.4232 17.9359C45.7917 18.2814 46.0144 18.7728 46.022 19.2795C46.0297 19.7863 45.8301 20.293 45.4692 20.6539L43.4806 22.6502C44.0027 24.6157 44.8627 28.8463 43.6419 32.1632C42.0679 36.4245 38.6128 39.5418 34.1595 40.7089C32.9464 41.0237 31.7179 41.1849 30.4971 41.1849L30.4894 41.1695ZM23.1953 35.4341C25.9978 37.8142 29.8061 38.6665 33.3763 37.7298C36.8545 36.8161 39.5418 34.3975 40.7626 31.0883C41.6072 28.7926 41.0697 25.2069 40.348 22.8421C40.1484 22.1741 40.325 21.4524 40.824 20.961L42.4364 19.3486C41.7991 18.7728 41.3077 18.3428 41.039 18.1432C38.7279 16.4156 35.9945 15.1948 33.5453 14.7879C27.0727 13.7283 22.1511 17.6287 20.1394 21.9668C18.3889 25.7444 18.2814 31.2649 23.1876 35.4341H23.1953Z"
      fill="white"
    />
    <path
      d="M43.1198 46.5134C44.7396 46.5134 46.0528 45.2003 46.0528 43.5804C46.0528 41.9606 44.7396 40.6474 43.1198 40.6474C41.4999 40.6474 40.1867 41.9606 40.1867 43.5804C40.1867 45.2003 41.4999 46.5134 43.1198 46.5134Z"
      fill="#97FAFF"
    />
  </svg>
);

const AppShellLayoutInner: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const admin = isAdmin(user);
  const { isFirmAdmin } = useAdminAccess();
  const { can } = useAccess();
  // GO Lab (Sep-3): a GO Lab capability only — the legacy-admin escape hatch is
  // gone (an org's legacy admin must not see internals; internal staff hold
  // PLATFORM_ADMIN / LAB_* which carry golab caps).
  const golabAccess = can(Capability.GolabAnnotate);
  // Monitoring: strictly the monitoring.view capability — NO legacy-admin escape
  // hatch. A platform admin holds only internal caps, so in its own org it has no
  // monitoring.view and the backend 403s /today/metrics; showing the nav would
  // just lead to an error page. When it acts as a client org (god mode) the
  // profile reports monitoring.view, so the nav reappears there. Restrict-by-default.
  const monitoringAccess = can(Capability.MonitoringView);

  const visibleItems = navItems.filter((item) => {
    if (item.adminOnly && !admin) return false;
    if (item.platform && !can(Capability.PlatformManage)) return false;
    if (item.golab && !golabAccess) return false;
    // Settings entry: org admins AND platform admins (who own the internal
    // settings surfaces gated above), plus audit-view users — the Audit Trail
    // page lives inside Settings, so an audit-only principal (firm.audit_view
    // without firm.manage) needs this entry to reach it, matching the
    // audit-trail route's firm_audit scope. Inside Settings, V3SettingsLayout
    // still gates every other item, so they see only Details + Audit Trail.
    if (
      item.firmAdmin &&
      !isFirmAdmin &&
      !can(Capability.PlatformManage) &&
      !can(Capability.FirmAuditView)
    )
      return false;
    if (item.monitoring && !monitoringAccess) return false;
    return true;
  });

  const isItemActive = (item: NavItem) => {
    const matchPaths = item.matchPaths ?? [item.path];
    return matchPaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  };

  return (
    <div className="flex h-screen bg-[var(--go-spring)] text-foreground">
      <aside className="flex w-14 flex-shrink-0 flex-col border-r border-border bg-background">
        <div className="flex h-14 items-center justify-center border-b border-border">
          <NavLink
            to="/v4/today"
            className="flex items-center justify-center transition-transform hover:scale-[1.03]"
            title="Good Outcomes"
          >
            <GoodOutcomesMark />
          </NavLink>
        </div>

        <nav className="flex flex-1 flex-col items-center gap-2 px-2 py-4">
          {visibleItems.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                title={item.label}
                className={() =>
                  cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg border border-transparent text-muted-foreground transition-[background-color,color,border-color,box-shadow]',
                    'hover:border-primary/15 hover:bg-accent hover:text-foreground',
                    isItemActive(item) && 'border-primary/15 bg-accent text-primary shadow-[var(--shadow-card)]'
                  )
                }
              >
                <Icon className="h-4 w-4" />
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border px-2 py-3">
          <div className="flex justify-center">
            <UserProfile isCollapsed />
          </div>
        </div>
      </aside>

      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-[var(--go-spring)]">
        <CrossOrgBar />
        <main className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const AppShellLayout: React.FC = () => {
  return (
    <JunoProvider>
      <PageHeaderProvider>
        <NavigationProvider>
          <AppShellLayoutInner />
          <Toaster />
        </NavigationProvider>
      </PageHeaderProvider>
    </JunoProvider>
  );
};

export default AppShellLayout;
